import type { ChangeEvent } from 'react';
import type { SlideProps } from '@mui/material';
import { routes } from '../../app/router/routes';
import Slide from '@mui/material/Slide';
import type { ReferenceItem } from '../../app/types';
import type { PractitionerFilterState } from '../../app/providers/PractitionerFilterProvider';
import type { PractitionerActivityDetail } from '../../features/practitioner/types';
import type {
  FilterReferenceList,
  FilterScalarValue,
  FilterTextValue,
} from '../../features/practitioner/lib/filterTypes';
import type { ExperienceSelectedItem } from '../ui/MultipleAddCheckboxes';
import {
  convertPractitionerActivitiesToArray,
  convertPractitionerActivitiesToObject,
} from '../../features/practitioner/lib/activities';

export type RouteKey = keyof typeof routes;

type SpecialistFilterState = PractitionerFilterState &
  Partial<PractitionerActivityDetail> & {
    audit?: boolean;
  };

interface GoogleFormattedAddress {
  formatted_address: string;
}

interface MapCoordinate {
  lat: number;
  lng: number;
}

export interface FormattedAddressResult {
  address: string;
  latitude: number;
  longitude: number;
}

export interface ParsedPostalAddress {
  street: string;
  postalCode: string;
  city: string;
  fullAddress: string;
}

interface GeocoderAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

interface GeocoderResultLike {
  formatted_address?: string;
  address_components?: GeocoderAddressComponent[];
}

export function parseGeocoderResult(result: GeocoderResultLike): ParsedPostalAddress {
  const components = result.address_components ?? [];

  const getComponent = (...types: string[]) =>
    components.find((component) => types.some((type) => component.types.includes(type)))?.long_name ?? '';

  const streetNumber = getComponent('street_number');
  const route = getComponent('route');
  const postalCode = getComponent('postal_code');
  const city = getComponent('locality', 'postal_town', 'administrative_area_level_2');
  const streetParts = [streetNumber, route].filter(Boolean);
  const street = streetParts.join(' ')
    || result.formatted_address?.split(',')[0]?.trim()
    || result.formatted_address
    || '';

  return {
    street,
    postalCode,
    city,
    fullAddress: result.formatted_address ?? street,
  };
}

interface ActivityTabItem {
  id: string;
  name?: string;
  '@type'?: string;
}

interface ExperienceApiItem {
  experience?: { name?: string; '@id'?: string };
  description?: string;
}

export interface FormattedExperienceItem {
  name: string;
  description?: string;
  experience?: string;
}

type MultipleTextFieldItem = ReferenceItem | ExperienceSelectedItem;

export function isKnownRoutes(route: string, get?: false): RouteKey | undefined;
export function isKnownRoutes(route: string, get: true): string;
export function isKnownRoutes(route: string, get = false): RouteKey | string | undefined {
    const r = route.lastIndexOf('/')
    let formattedRoute = route
    if (r > 0) {
        formattedRoute = route.substring(0,r) + '/:id'
    }
    if (get) {
        return formattedRoute
    }
    return (Object.keys(routes) as RouteKey[]).find((key) => routes[key] === formattedRoute);
}

export function TransitionLeft(props: SlideProps) {
    return <Slide {...props} direction="left" />;
}

export const progressBarWidth = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    setStyle: (style: { width: number; color: string }) => void,
) => {
    let percentage = 0
    let background = "#dd4b39"

    if (e.target.value.length < 4) {
        percentage = 0;
        background = "#dd4b39";
    } else if (e.target.value.length < 6) {
        percentage = 30;
        background = "#ff9800";
    } else if (e.target.value.length < 8) {
        percentage = 50;
        background = "#ff9800";
    } else {
        percentage = 70;
        background = "#4caf50";
    }

    if ((e.target.value.match(/[a-z]/) != null)) {
        percentage += 10;
    }
    
    if ((e.target.value.match(/[A-Z]/) != null)) {
        percentage += 10;
    }
    
    if ((e.target.value.match(/0|1|2|3|4|5|6|7|8|9/) != null)) {
        percentage += 10;
    }

    setStyle({width: percentage, color: background})
}

export function removeLastComma(strng: string): string {  
    const n=strng.lastIndexOf(",");
    if (n > 0) {
        const a=strng.substring(0,n)
        return a;
    }
    return strng
}

type MapCoordinateInput = MapCoordinate | { lat?: number; lng?: number } | null | undefined;

type FormatAddressInput =
  | GoogleFormattedAddress[]
  | { formatted_address?: string }[]
  | { address?: string; latitude?: number | string; longitude?: number | string; fullAddress?: string }
  | null
  | undefined;

export function formatAddress(
    newAddress: FormatAddressInput,
    newCoordonate: MapCoordinateInput,
): FormattedAddressResult | null {
    if (!newAddress || !newCoordonate) return null;

    const latitude = 'lat' in newCoordonate ? newCoordonate.lat : undefined;
    const longitude = 'lng' in newCoordonate ? newCoordonate.lng : undefined;

    if (Array.isArray(newAddress) && newAddress.length > 0 && newAddress[0]?.formatted_address) {
        return {
            address: newAddress[0].formatted_address,
            latitude: latitude ?? 0,
            longitude: longitude ?? 0,
        };
    }

    if (!Array.isArray(newAddress) && typeof newAddress === 'object' && newAddress.address) {
        const lat = newAddress.latitude;
        const lng = newAddress.longitude;
        return {
            address: newAddress.address,
            latitude: typeof lat === 'string' ? parseFloat(lat) : (lat ?? latitude ?? 0),
            longitude: typeof lng === 'string' ? parseFloat(lng) : (lng ?? longitude ?? 0),
        };
    }

    return null;
}

export function isEquivalent(
    a: Record<string, unknown>,
    b: Record<string, unknown>,
): false | Record<string, unknown> {
    const aProps = Object.getOwnPropertyNames(a);
    const bProps = Object.getOwnPropertyNames(b);

    const element: Record<string, unknown> = {}
    if (aProps.length !== bProps.length) {
        return false;
    }

    for (let i = 0; i < aProps.length; i++) {
        const propName = aProps[i];

        if (a[propName] !== b[propName]) {
            element[propName] = b[propName]
        }
    }

    return Object.keys(element).length === 0 ? false : element;
}

export function download(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

export function removeLastAmpersand(strng: string): string {  
    const n=strng.charAt(strng.length-1);
    if (n === '&') {
        const a=strng.substring(0, strng.length - 1)
        return a;
    }
    return strng
}

export function translateStatus(status: string): string {
    switch(status) {
        case 'Pending':
            return 'En attente'
        case 'Canceled':
            return 'Annulée'
        default:
            return 'Validée'
    }
}

export function getRightColor(status: string): string {
    switch(status) {
        case "valide":
            return 'rgba(0, 160, 38, 0.7)'
        case "renouvellement dépassé":
            return 'rgba(0, 0, 0, 0.7)'
        case "renouvellement urgent":
            return "rgba(255, 0, 0, 0.7)"
        default:
            return 'rgba(234, 161, 16, 0.7)'
    }
}

export function formatMultipleCompanies(c: ReferenceItem[]): string[] {
    const tab: string[] = []
    c.map((value) => {
        if (value['@id']) {
            return tab.push(value['@id'])
        }
        return null
    })
    return tab
}

export function formatMultipleOptions(identifier: string, options: ReferenceItem[]): string {
    let s =''
    options.map((option) => {
        if (option) {
            return s += `${identifier}[]=${option.name}&`
        }
        return null
    })
    return s
}

export function handleFilter(
    speciality: FilterReferenceList,
    firstName: FilterTextValue,
    lastName: FilterTextValue,
    language: FilterReferenceList,
    administrativeType: FilterReferenceList,
    specialistType: FilterReferenceList,
    certification: FilterReferenceList,
    state: SpecialistFilterState,
) {
    const chosenSpecialities = speciality ? formatMultipleOptions('specialities.name', speciality) : ''
    const foundFirstName = firstName ? `firstName=${firstName}&` : ''
    const foundLastName = lastName ? `lastName=${lastName}&` : ''
    const languagesFilter = language ? formatMultipleOptions('languages.name', language) : ''
    const administrativeTypeFilter = administrativeType ? formatMultipleOptions('administrativeTypes.name', administrativeType) : ''
    const specialistTypeFilter = specialistType ? formatMultipleOptions('specialistType.name',specialistType) : ''
    const certificationFilter = certification ? formatMultipleOptions('certifications.name',certification) : ''
    const officeFilter = state.office ? `offices=${state.office}&` : ''
    const homeInterventionFilter = state.homeIntervention ? `activity.homeInterventions=${state.homeIntervention}&` : ''
    const appFilter = state.app ? `activity.app=${state.app}&` : ''
    const auditFilter = state.audit ? `activity.audit=${state.audit}&` : ''
    const coachingFilter = state.coaching ? `activity.coaching=${state.coaching}&` : ''
    const conflictResolutionSupportFilter = state.conflictResolutionSupport ? `activity.conflictResolutionSupport=${state.conflictResolutionSupport}&` : ''
    const crisisUnitFilter = state.crisisUnit ? `activity.crisisUnit=${state.crisisUnit}&` : ''
    const harassmentInvestigationFilter = state.harassmentInvestigation ? `activity.harassmentInvestigation=${state.harassmentInvestigation}&` : ''
    const certifiedCoachingFilter = state.certifiedCoaching ? `activity.certifiedCoaching=${state.certifiedCoaching}&` : ''
    const platformFilter = state.platform ? `activity.platform=${state.platform}&` : ''
    const projectManagerFilter = state.projectManager ? `activity.projectManager=${state.projectManager}&` : ''
    const pseFilter = state.pse ? `activity.pse=${state.pse}&` : ''
    const gpVisioFilter = state.gpVisio ? `activity.visio=${state.gpVisio}&` : ''
    const gpAudioFilter = state.gpAudio ? `activity.call=${state.gpAudio}&` : ''
    const passedPrestaFilter = state.passedPresta ? `passedPresta=true&` : ''
    return {
        chosenSpecialities,
        foundFirstName,
        foundLastName,
        languagesFilter,
        administrativeTypeFilter,
        specialistTypeFilter,
        certificationFilter,
        officeFilter,
        homeInterventionFilter,
        appFilter,
        auditFilter,
        coachingFilter,
        conflictResolutionSupportFilter,
        crisisUnitFilter,
        harassmentInvestigationFilter,
        platformFilter,
        projectManagerFilter,
        pseFilter,
        gpVisioFilter,
        gpAudioFilter,
        certifiedCoachingFilter,
        passedPrestaFilter,
    }
}

export function handleOfficeSpecialistFilter(
    officeSpecialistType: FilterScalarValue,
    officeSpecialistSpecialities: FilterScalarValue,
    officeSpecialistTarget: FilterScalarValue,
    firstName: FilterTextValue,
    lastName: FilterTextValue,
) {
    const officeSpecialistTypeFilter = officeSpecialistType ? `specialistType=${officeSpecialistType}&` : ''
    const officeSpecialistSpecialitiesFilter = officeSpecialistSpecialities ? `specialities={${officeSpecialistSpecialities}}&` : ''
    const officeSpecialistTargetFilter = officeSpecialistTarget ?  `sepcialistTarget={${officeSpecialistTarget}}&` : ''
    const foundFirstName = firstName ? `firstName=${firstName}&` : ''
    const foundLastName = lastName ? `lastName=${lastName}&` : ''

    return {
        officeSpecialistTypeFilter,
        officeSpecialistSpecialitiesFilter,
        officeSpecialistTargetFilter,
        foundFirstName,
        foundLastName,
    }
}

export function handleMultipleTextFieldValue(
    value: MultipleTextFieldItem[],
    experiences: boolean | undefined,
): unknown[] {
    const tab: unknown[] = []
    if (experiences) {
        value.map((item) => {
            const experienceItem = item as ExperienceSelectedItem;
            if (experienceItem.isNew) {
                tab.push({
                    experience: {
                        name: experienceItem.name
                    },
                    description: experienceItem.description            
                })
            } else {
                const experienceRef = experienceItem.experience;
                tab.push({
                    experience: experienceItem.isAdd && experienceRef && typeof experienceRef === 'object' && '@id' in experienceRef
                        ? experienceRef['@id']
                        : experienceRef,
                    description: experienceItem.description
                })
            }
            return null
        })
    } else {
        value.length && value.map((i) => tab.push(i['@id']))
    }
    return tab
}

export function convertActivitiesToObject(tab: ActivityTabItem[]) {
    return convertPractitionerActivitiesToObject(tab);
}

export function convertActivitiesToArray(object: PractitionerActivityDetail): ReferenceItem[] {
    return convertPractitionerActivitiesToArray(object);
}

export {
  convertPractitionerActivitiesToArray,
  convertPractitionerActivitiesToObject,
  getPractitionerActivityLabel,
  PRACTITIONER_ACTIVITIES,
  PRACTITIONER_ACTIVITY_IDS,
} from '../../features/practitioner/lib/activities';

export function formatExperiences(
    data: ExperienceApiItem[] | false | null | undefined,
): FormattedExperienceItem[] {
    const tab: FormattedExperienceItem[] = []
    data && data.map((item) => tab.push({
        name: item.experience?.name ?? '',
        description: item.description,
        experience: item.experience && item.experience['@id'],
    }))
    return tab
}
